/* LoveLia cart drawer — markup in sections/lovelia-cart-drawer.liquid, styles in lovelia-cart-drawer.css */
(() => {
  if (customElements.get('lovelia-cart-drawer')) return;

  const SECTION = 'lovelia-cart-drawer';
  const RECS_SECTION = 'lovelia-cart-recommendations';
  const BUBBLE = 'cart-icon-bubble';
  const OWN_HEADER = 'X-Lovelia-Cart';
  const CART_ENDPOINT = /\/cart\/(add|change|update|clear)(?:\.js)?$/;
  const EASE = 'cubic-bezier(.22, 1, .36, 1)';
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const duration = (ms) => (reducedMotion.matches ? 0 : ms);

  const cartAction = (url) => {
    try {
      const match = new URL(url, window.location.href).pathname.match(CART_ENDPOINT);
      return match ? match[1] : null;
    } catch (error) {
      return null;
    }
  };

  const hasOwnHeader = (input, init) => {
    const headers = (init && init.headers) || (input instanceof Request ? input.headers : null);
    if (!headers) return false;
    if (headers instanceof Headers) return headers.has(OWN_HEADER);
    if (Array.isArray(headers)) return headers.some(([key]) => key.toLowerCase() === OWN_HEADER.toLowerCase());
    return Object.keys(headers).some((key) => key.toLowerCase() === OWN_HEADER.toLowerCase());
  };

  const notifyDrawer = (action) => {
    const drawer = document.querySelector('lovelia-cart-drawer');
    if (drawer && typeof drawer.onExternalCartChange === 'function') drawer.onExternalCartChange(action);
  };

  // Cart changes made by the theme or apps (product forms, bundles, quick add) refresh and open the drawer.
  // Installed once; the drawer's own requests carry OWN_HEADER and are ignored here.
  const watchCartRequests = () => {
    if (window.__loveliaCartWatching) return;
    window.__loveliaCartWatching = true;

    const previousFetch = window.fetch;
    window.fetch = function (input, init) {
      const url = typeof input === 'string' ? input : input instanceof URL ? input.href : input && input.url;
      const action = url ? cartAction(url) : null;
      const request = previousFetch.apply(window, arguments);
      if (action && !hasOwnHeader(input, init)) {
        request.then((response) => response.ok && notifyDrawer(action)).catch(() => {});
      }
      return request;
    };

    const xhrOpen = XMLHttpRequest.prototype.open;
    const xhrSend = XMLHttpRequest.prototype.send;
    XMLHttpRequest.prototype.open = function (method, url) {
      this.__loveliaCartAction = cartAction(String(url));
      return xhrOpen.apply(this, arguments);
    };
    XMLHttpRequest.prototype.send = function () {
      const action = this.__loveliaCartAction;
      if (action) {
        this.addEventListener('load', () => {
          if (this.status >= 200 && this.status < 300) notifyDrawer(action);
        });
      }
      return xhrSend.apply(this, arguments);
    };
  };

  class LoveliaCartDrawer extends HTMLElement {
    connectedCallback() {
      if (this.abortController) return;
      this.abortController = new AbortController();
      const signal = this.abortController.signal;

      this.panel = this.querySelector('[data-lc-panel]');
      this.strings = this.readJSON('[data-lc-strings]') || {};
      this.pendingLines = new Map();
      this.isOpen = false;

      this.addEventListener('click', (event) => this.onClick(event), { signal });
      this.addEventListener('change', (event) => this.onChange(event), { signal });
      this.addEventListener('submit', (event) => this.onSubmit(event), { signal });
      // On document: focus can fall back to <body> when a focused element is removed (e.g. an added recommendation)
      document.addEventListener('keydown', (event) => this.isOpen && this.onKeydown(event), { signal });
      const track = this.querySelector('[data-lc-recs-track]');
      if (track) track.addEventListener('scroll', () => this.updateArrows(), { passive: true, signal });

      // Capture phase so the theme's own cart icon handler never opens the hidden theme drawer
      document.addEventListener('click', (event) => this.onDocumentClick(event), { capture: true, signal });
      document.addEventListener('cart:refresh', () => this.refresh(), { signal });
      window.addEventListener('pageshow', () => this.resetCheckoutButton(), { signal });

      if (window.Shopify && window.Shopify.designMode) {
        const isThis = (event) => event.detail && event.detail.sectionId === this.dataset.sectionId;
        document.addEventListener('shopify:section:select', (event) => isThis(event) && this.open(), { signal });
        document.addEventListener('shopify:section:deselect', (event) => isThis(event) && this.close(), { signal });
      }

      this.suppressThemeDrawer();
      watchCartRequests();
      this.updateProgress(false);

      window.LoveliaCart = {
        open: () => this.open(),
        close: () => this.close(),
        refresh: () => this.refresh(),
      };
    }

    disconnectedCallback() {
      if (this.abortController) this.abortController.abort();
      if (this.themeDrawerObserver) this.themeDrawerObserver.disconnect();
      document.documentElement.classList.remove('lc-locked');
    }

    readJSON(selector, root = this) {
      const node = root.querySelector(selector);
      if (!node) return null;
      try {
        return JSON.parse(node.textContent);
      } catch (error) {
        return null;
      }
    }

    /* ---------- Open / close ---------- */

    async open() {
      if (this.isOpen) return;
      this.isOpen = true;
      clearTimeout(this.hideTimer);
      this.lastFocus = document.activeElement;

      await this.ensureStyles();
      if (!this.isOpen) return;

      const root = document.documentElement;
      root.style.setProperty('--lc-scrollbar', `${window.innerWidth - root.clientWidth}px`);
      root.classList.add('lc-locked');

      this.hidden = false;
      this.resetProgressBar();
      void this.offsetWidth; // commit the closed state so the slide-in transition runs
      requestAnimationFrame(() => {
        this.classList.add('is-open');
        this.staggerIn();
        this.updateProgress(true);
      });
      this.panel.focus({ preventScroll: true });
      this.loadRecommendations();
      this.dispatchEvent(new CustomEvent('lovelia-cart:open', { bubbles: true }));
    }

    close() {
      if (!this.isOpen) return;
      this.isOpen = false;
      this.classList.remove('is-open');
      this.hideTimer = setTimeout(() => {
        if (this.isOpen) return;
        this.hidden = true;
        document.documentElement.classList.remove('lc-locked');
        document.documentElement.style.removeProperty('--lc-scrollbar');
      }, duration(520));
      if (this.lastFocus && typeof this.lastFocus.focus === 'function' && document.contains(this.lastFocus)) {
        this.lastFocus.focus({ preventScroll: true });
      }
      this.dispatchEvent(new CustomEvent('lovelia-cart:close', { bubbles: true }));
    }

    // The stylesheet loads without blocking render; make sure it is applied before the first open.
    ensureStyles() {
      const link = document.querySelector('link[href*="lovelia-cart-drawer"][rel="stylesheet"]');
      if (!link || (link.sheet && link.media === 'all')) return Promise.resolve();
      link.media = 'all';
      if (link.sheet) return Promise.resolve();
      return new Promise((resolve) => {
        link.addEventListener('load', resolve, { once: true });
        link.addEventListener('error', resolve, { once: true });
        setTimeout(resolve, 1500);
      });
    }

    staggerIn() {
      if (reducedMotion.matches || !this.panel.animate) return;
      const selector = '[data-lc-anim], .lc__upsells > *, .lc-items > .lc-item, .lc-empty:not(:empty), .lc-recs:not([hidden])';
      [...this.panel.querySelectorAll(selector)]
        .filter((element) => !element.closest('[hidden]'))
        .slice(0, 12)
        .forEach((element, index) => {
          element.animate(
            [
              { opacity: 0, transform: 'translate3d(18px, 0, 0)' },
              { opacity: 1, transform: 'none' },
            ],
            { duration: 520, delay: 90 + index * 40, easing: EASE, fill: 'backwards' }
          );
        });
    }

    onDocumentClick(event) {
      const trigger = event.target.closest && event.target.closest('.header__icon--cart, [data-lovelia-cart-open]');
      if (!trigger || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      this.open();
    }

    // Shrine's product forms still talk to the theme's <cart-drawer>; whenever it tries to open, close it
    // and show this drawer instead.
    suppressThemeDrawer() {
      const themeDrawer = document.querySelector('cart-drawer');
      if (!themeDrawer) return;
      this.themeDrawerObserver = new MutationObserver(() => {
        if (!themeDrawer.classList.contains('active')) return;
        try {
          if (typeof themeDrawer.close === 'function') themeDrawer.close();
        } catch (error) {
          /* theme close failed; clean up manually below */
        }
        themeDrawer.classList.remove('active', 'animate');
        document.body.classList.remove('overflow-hidden');
        this.open();
        this.scheduleRefresh();
      });
      this.themeDrawerObserver.observe(themeDrawer, { attributes: true, attributeFilter: ['class'] });
    }

    onExternalCartChange(action) {
      if (action === 'add') this.open();
      this.scheduleRefresh();
    }

    scheduleRefresh() {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = setTimeout(() => this.refresh(), 60);
    }

    /* ---------- Events ---------- */

    onClick(event) {
      const target = event.target;
      if (target.closest('[data-lc-close]')) {
        event.preventDefault();
        this.close();
        return;
      }
      const tab = target.closest('[data-lc-tab]');
      if (tab) return this.setTab(tab.dataset.lcTab);

      const qty = target.closest('[data-lc-qty]');
      if (qty) return this.onQuantity(qty);

      const remove = target.closest('[data-lc-remove]');
      if (remove) return this.setLineQuantity(remove.closest('.lc-item'), 0);

      const add = target.closest('[data-lc-add]');
      if (add) return this.onAdd(add);

      const removeCode = target.closest('[data-lc-remove-code]');
      if (removeCode) return this.removeDiscount(removeCode.dataset.lcRemoveCode);

      const nav = target.closest('[data-lc-recs-nav]');
      if (nav) return this.scrollRecs(Number(nav.dataset.lcRecsNav));
    }

    onChange(event) {
      if (event.target.matches('[data-lc-option]')) this.onOptionChange(event.target);
    }

    onSubmit(event) {
      const form = event.target;
      if (form.matches('[data-lc-discount-form]')) {
        event.preventDefault();
        this.applyDiscount(form);
      } else if (form.matches('[data-lc-checkout-form]')) {
        const button = form.querySelector('button[type="submit"]');
        if (button.classList.contains('is-loading')) return event.preventDefault();
        button.classList.add('is-loading');
      }
    }

    resetCheckoutButton() {
      const button = this.querySelector('[data-lc-checkout-form] button');
      if (button) button.classList.remove('is-loading');
    }

    onKeydown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        return;
      }

      if ((event.key === 'ArrowRight' || event.key === 'ArrowLeft') && event.target.matches('[data-lc-tab]')) {
        const tabs = [...this.querySelectorAll('[data-lc-tab]')];
        const next = tabs[(tabs.indexOf(event.target) + (event.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length];
        this.setTab(next.dataset.lcTab);
        next.focus();
        return;
      }

      if (event.key !== 'Tab') return;
      const focusable = this.focusableElements();
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;
      if (!this.panel.contains(active)) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
      } else if (event.shiftKey && (active === first || active === this.panel)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    }

    focusableElements() {
      return [
        ...this.panel.querySelectorAll(
          'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
        ),
      ].filter((element) => element.getClientRects().length > 0 && !element.closest('[hidden]'));
    }

    /* ---------- Tabs ---------- */

    setTab(name) {
      const tabs = this.querySelector('.lc__tabs');
      if (!tabs || tabs.dataset.active === name) return;
      tabs.dataset.active = name;
      tabs.querySelectorAll('[data-lc-tab]').forEach((tab) => {
        const selected = tab.dataset.lcTab === name;
        tab.setAttribute('aria-selected', String(selected));
        tab.tabIndex = selected ? 0 : -1;
      });
      this.querySelectorAll('[data-lc-view]').forEach((view) => {
        clearTimeout(view.hideTimer);
        if (view.dataset.lcView === name) {
          view.hidden = false;
          void view.offsetWidth;
          view.classList.add('is-active');
          view.scrollTop = 0;
        } else {
          view.classList.remove('is-active');
          view.hideTimer = setTimeout(() => {
            if (!view.classList.contains('is-active')) view.hidden = true;
          }, duration(450));
        }
      });
    }

    /* ---------- Cart requests ---------- */

    rootUrl() {
      return this.dataset.rootUrl || '/';
    }

    async cartRequest(action, body) {
      const routes = window.routes || {};
      const base =
        { add: routes.cart_add_url, change: routes.cart_change_url, update: routes.cart_update_url }[action] ||
        `${this.rootUrl().replace(/\/$/, '')}/cart/${action}`;
      const response = await fetch(`${base}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json', [OWN_HEADER]: '1' },
        body: JSON.stringify({ ...body, sections: [SECTION, BUBBLE], sections_url: window.location.pathname }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || data.status) {
        const error = new Error(data.description || data.message || this.strings.error);
        error.data = data;
        throw error;
      }
      return data;
    }

    async refresh() {
      const root = this.rootUrl();
      const url = `${root === '/' ? '/' : root}?sections=${SECTION},${BUBBLE}`;
      try {
        const response = await fetch(url, {
          headers: { Accept: 'application/json', [OWN_HEADER]: '1' },
          cache: 'no-store',
        });
        if (!response.ok) return;
        this.renderSections(await response.json());
      } catch (error) {
        /* network hiccup: the next cart action re-renders */
      }
    }

    renderSections(sections) {
      const html = sections && sections[SECTION];
      if (!html) return this.refresh();
      const next = new DOMParser().parseFromString(html, 'text/html').querySelector('lovelia-cart-drawer');
      if (!next) return;
      this.applyState(next);

      const bubbleHtml = sections[BUBBLE];
      const bubble = document.getElementById(BUBBLE);
      if (bubbleHtml && bubble) {
        const doc = new DOMParser().parseFromString(bubbleHtml, 'text/html');
        bubble.innerHTML = (doc.querySelector('.shopify-section') || doc.body).innerHTML;
      }
      document.dispatchEvent(
        new CustomEvent('lovelia-cart:updated', { detail: { itemCount: Number(this.dataset.itemCount) } })
      );
    }

    applyState(next) {
      const previousRecsProduct = this.dataset.recsProduct;
      ['itemCount', 'inCart', 'recsProduct', 'appliedCodes'].forEach((key) => {
        this.dataset[key] = next.dataset[key] || '';
      });
      this.classList.toggle('is-empty', next.classList.contains('is-empty'));

      next.querySelectorAll('[data-region]').forEach((nextRegion) => {
        const name = nextRegion.dataset.region;
        if (name === 'items') return;
        const region = this.querySelector(`[data-region="${name}"]`);
        if (!region) return;
        if (name === 'offers-dot') {
          region.hidden = nextRegion.hidden;
          return;
        }
        if (region.innerHTML === nextRegion.innerHTML) return;
        region.innerHTML = nextRegion.innerHTML;
        if (name === 'title') this.bump(region);
        if (name === 'upsell' || name === 'offers') this.fadeChildren(region);
        if (name === 'totals') this.fadeChildren(region, 200);
      });

      const nextProgress = next.querySelector('[data-lc-progress]');
      const progress = this.querySelector('[data-lc-progress]');
      if (nextProgress && progress) {
        progress.dataset.total = nextProgress.dataset.total;
        progress.dataset.threshold = nextProgress.dataset.threshold;
        this.updateProgress(this.isOpen);
      }

      this.diffItems(this.querySelector('[data-region="items"]'), next.querySelector('[data-region="items"]'));

      if (previousRecsProduct !== this.dataset.recsProduct) this.recsStale = true;
      if (this.isOpen) this.loadRecommendations();
    }

    // Keyed update of the line items so unchanged rows stay put and changes animate.
    diffItems(list, nextList) {
      if (!list || !nextList) return;
      const current = new Map();
      [...list.children].forEach((item) => {
        if (!item.leaving) current.set(item.dataset.lineKey, item);
      });
      const nextItems = [...nextList.children];
      const nextKeys = new Set(nextItems.map((item) => item.dataset.lineKey));

      current.forEach((item, key) => {
        if (!nextKeys.has(key)) this.animateOut(item);
      });

      let previous = null;
      nextItems.forEach((nextItem) => {
        const key = nextItem.dataset.lineKey;
        let item = current.get(key);
        if (item) {
          if (item.innerHTML !== nextItem.innerHTML || item.dataset.quantity !== nextItem.dataset.quantity) {
            const focusedQty = item.contains(document.activeElement) && document.activeElement.dataset.lcQty;
            item.innerHTML = nextItem.innerHTML;
            item.dataset.quantity = nextItem.dataset.quantity;
            if (focusedQty) {
              const button = item.querySelector(`[data-lc-qty="${focusedQty}"]`);
              if (button) button.focus({ preventScroll: true });
            }
          }
          const pending = this.pendingLines.get(key);
          if (pending) item.querySelector('[data-lc-qty-value]').textContent = pending.quantity;
        } else {
          item = document.importNode(nextItem, true);
          this.animateIn(item);
        }
        if (previous) {
          if (previous.nextElementSibling !== item) previous.after(item);
        } else if (list.firstElementChild !== item) {
          list.prepend(item);
        }
        previous = item;
      });
    }

    animateIn(item) {
      if (!item.animate || reducedMotion.matches) return;
      requestAnimationFrame(() => {
        item.animate(
          [
            { opacity: 0, transform: 'translate3d(0, 12px, 0)', backgroundColor: `rgba(${this.primaryRgb()}, .08)` },
            { opacity: 1, transform: 'none', backgroundColor: `rgba(${this.primaryRgb()}, .08)`, offset: 0.6 },
            { opacity: 1, transform: 'none', backgroundColor: 'transparent' },
          ],
          { duration: 900, easing: EASE }
        );
      });
    }

    animateOut(item) {
      if (item.leaving) return;
      item.leaving = true;
      item.setAttribute('aria-hidden', 'true');
      if (!item.animate || reducedMotion.matches) {
        item.remove();
        return;
      }
      const height = item.offsetHeight;
      item.style.overflow = 'hidden';
      const animation = item.animate(
        [
          { height: `${height}px`, opacity: 1, transform: 'none' },
          { height: `${height}px`, opacity: 0, transform: 'translate3d(32px, 0, 0)', offset: 0.5 },
          { height: '0px', opacity: 0, transform: 'translate3d(32px, 0, 0)', paddingTop: '0px', paddingBottom: '0px' },
        ],
        { duration: 420, easing: EASE, fill: 'forwards' }
      );
      animation.onfinish = () => item.remove();
    }

    fadeChildren(region, delay = 0) {
      if (reducedMotion.matches || !region.animate) return;
      [...region.children].forEach((child, index) => {
        child.animate(
          [
            { opacity: 0, transform: 'translate3d(0, 6px, 0)' },
            { opacity: 1, transform: 'none' },
          ],
          { duration: 360, delay: delay + index * 40, easing: EASE, fill: 'backwards' }
        );
      });
    }

    bump(element) {
      element.classList.remove('is-bump');
      void element.offsetWidth;
      element.classList.add('is-bump');
    }

    primaryRgb() {
      if (!this.cachedPrimaryRgb) {
        this.cachedPrimaryRgb = getComputedStyle(this).getPropertyValue('--lc-primary-rgb').trim() || '109, 56, 139';
      }
      return this.cachedPrimaryRgb;
    }

    showAlert(message) {
      const alert = this.querySelector('[data-lc-alert]');
      if (!alert) return;
      alert.textContent = message || this.strings.error;
      alert.hidden = false;
      clearTimeout(this.alertTimer);
      this.alertTimer = setTimeout(() => (alert.hidden = true), 5000);
    }

    /* ---------- Quantity ---------- */

    onQuantity(button) {
      const item = button.closest('.lc-item');
      if (!item) return;
      const pending = this.pendingLines.get(item.dataset.lineKey);
      const current = pending && pending.quantity != null ? pending.quantity : Number(item.dataset.quantity);
      this.setLineQuantity(item, current + Number(button.dataset.lcQty));
    }

    setLineQuantity(item, requested) {
      if (!item || item.leaving) return;
      const key = item.dataset.lineKey;
      const pending = this.pendingLines.get(key) || {};
      const current = pending.quantity != null ? pending.quantity : Number(item.dataset.quantity);
      const quantity = Math.max(0, requested);
      if (quantity === current) return;

      pending.quantity = quantity;
      clearTimeout(pending.timer);
      this.pendingLines.set(key, pending);

      const value = item.querySelector('[data-lc-qty-value]');
      value.textContent = quantity;
      value.classList.remove('is-tick');
      void value.offsetWidth;
      value.classList.add('is-tick');
      this.setItemError(item, null);

      if (quantity === 0) this.animateOut(item);
      // Short debounce so fast taps become one request
      pending.timer = setTimeout(() => this.commitQuantity(key, item), quantity === 0 ? 0 : 350);
    }

    async commitQuantity(key, item) {
      const pending = this.pendingLines.get(key);
      if (!pending) return;
      const quantity = pending.quantity;
      item.classList.add('is-pending');
      try {
        const data = await this.cartRequest('change', { id: key, quantity });
        if (this.pendingLines.get(key) === pending && pending.quantity === quantity) this.pendingLines.delete(key);
        this.renderSections(data.sections);
      } catch (error) {
        this.pendingLines.delete(key);
        await this.refresh();
        const fresh = this.querySelector(`.lc-item[data-line-key="${CSS.escape(key)}"]`);
        if (fresh) this.setItemError(fresh, error.message);
        else this.showAlert(error.message);
      } finally {
        item.classList.remove('is-pending');
      }
    }

    setItemError(item, message) {
      const error = item && item.querySelector('[data-lc-item-error]');
      if (!error) return;
      error.textContent = message || '';
      error.hidden = !message;
    }

    /* ---------- Add (upsell, offers, recommendations) ---------- */

    onOptionChange(select) {
      const row = select.closest('[data-lc-product-row]');
      if (!row) return;
      if (!row.variants) row.variants = this.readJSON('[data-lc-variants]', row) || [];
      const values = [...row.querySelectorAll('[data-lc-option]')].map((option) => option.value);
      const variant = row.variants.find((candidate) => candidate.options.every((value, index) => value === values[index]));
      const button = row.querySelector('[data-lc-add]');
      const label = button.querySelector('[data-lc-btn-label]');
      const price = row.querySelector('[data-lc-row-price]');

      if (variant) {
        button.dataset.variantId = variant.id;
        if (price.textContent !== variant.price) {
          price.textContent = variant.price;
          if (price.animate && !reducedMotion.matches) {
            price.animate([{ opacity: 0, transform: 'translate3d(0, -4px, 0)' }, { opacity: 1, transform: 'none' }], {
              duration: 260,
              easing: EASE,
            });
          }
        }
      }
      const available = Boolean(variant && variant.available);
      button.disabled = !available;
      label.textContent = available ? button.dataset.label : this.strings.soldOut;
    }

    async onAdd(button) {
      if (button.disabled || button.classList.contains('is-loading')) return;
      button.classList.add('is-loading');
      button.disabled = true;
      try {
        const data = await this.cartRequest('add', { items: [{ id: Number(button.dataset.variantId), quantity: 1 }] });
        const rec = button.closest('[data-lc-rec]');
        if (rec) this.removeRec(rec);
        this.renderSections(data.sections);
        const scroller = this.querySelector('[data-lc-view="cart"]');
        if (scroller && !rec) scroller.scrollTo({ top: 0, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
      } catch (error) {
        button.disabled = false;
        this.showAlert(error.message);
      } finally {
        button.classList.remove('is-loading');
      }
    }

    /* ---------- Discount codes ---------- */

    appliedCodes() {
      return (this.dataset.appliedCodes || '')
        .split(',')
        .map((code) => code.trim())
        .filter(Boolean);
    }

    setDiscountError(message) {
      const form = this.querySelector('[data-lc-discount-form]');
      const error = this.querySelector('[data-lc-discount-error]');
      if (!form || !error) return;
      form.classList.remove('is-invalid');
      error.hidden = !message;
      error.textContent = message || '';
      if (message) {
        void form.offsetWidth;
        form.classList.add('is-invalid');
      }
    }

    async applyDiscount(form) {
      const input = form.querySelector('input[name="discount"]');
      const button = form.querySelector('button[type="submit"]');
      const code = input.value.trim();
      const existing = this.appliedCodes();

      if (!code) return this.setDiscountError(this.strings.discountEmpty);
      if (existing.some((applied) => applied.toLowerCase() === code.toLowerCase())) {
        return this.setDiscountError(this.strings.discountDuplicate);
      }

      this.setDiscountError(null);
      button.classList.add('is-loading');
      button.disabled = true;
      try {
        const data = await this.cartRequest('update', { discount: [...existing, code].join(',') });
        const match = (data.discount_codes || []).find((entry) => entry.code.toLowerCase() === code.toLowerCase());
        if (match && match.applicable) {
          input.value = '';
          this.renderSections(data.sections);
        } else {
          // Shopify keeps non-applicable codes on the cart; remove it again
          const reverted = await this.cartRequest('update', { discount: existing.join(',') });
          this.renderSections(reverted.sections);
          this.setDiscountError(this.strings.discountInvalid);
        }
      } catch (error) {
        this.setDiscountError(error.message);
      } finally {
        button.classList.remove('is-loading');
        button.disabled = false;
      }
    }

    async removeDiscount(code) {
      const remaining = this.appliedCodes().filter((applied) => applied !== code);
      const pill = this.querySelector(`[data-lc-remove-code="${CSS.escape(code)}"]`);
      if (pill) pill.closest('.lc-code').style.opacity = '.5';
      try {
        const data = await this.cartRequest('update', { discount: remaining.join(',') });
        this.setDiscountError(null);
        this.renderSections(data.sections);
      } catch (error) {
        if (pill) pill.closest('.lc-code').style.opacity = '';
        this.setDiscountError(error.message);
      }
    }

    /* ---------- Free shipping bar ---------- */

    resetProgressBar() {
      const bar = this.querySelector('[data-lc-progress-bar]');
      if (!bar) return;
      bar.style.transition = 'none';
      bar.style.transform = 'scaleX(0)';
      void bar.offsetWidth;
      bar.style.transition = '';
    }

    updateProgress(animate) {
      const progress = this.querySelector('[data-lc-progress]');
      if (!progress) return;
      const currency = window.Shopify && window.Shopify.currency;
      const rate = (currency && parseFloat(currency.rate)) || 1;
      const threshold = Math.round((parseInt(progress.dataset.threshold, 10) || 0) * rate);
      const total = parseInt(progress.dataset.total, 10) || 0;
      const remaining = Math.max(0, threshold - total);
      const ratio = threshold > 0 ? Math.min(1, total / threshold) : 1;
      const html =
        remaining > 0
          ? progress.dataset.textRemaining.replace('[amount]', this.formatMoney(remaining))
          : progress.dataset.textDone;

      const text = progress.querySelector('[data-lc-progress-text]');
      if (text.innerHTML !== html) {
        if (animate && text.innerHTML && !reducedMotion.matches) {
          text.classList.add('is-swapping');
          clearTimeout(this.progressTimer);
          this.progressTimer = setTimeout(() => {
            text.innerHTML = html;
            text.classList.remove('is-swapping');
          }, 180);
        } else {
          text.innerHTML = html;
        }
      }

      const bar = progress.querySelector('[data-lc-progress-bar]');
      if (!animate) bar.style.transition = 'none';
      bar.style.transform = `scaleX(${ratio})`;
      if (!animate) {
        void bar.offsetWidth;
        bar.style.transition = '';
      }
    }

    // Formats cents like the shop's money format, learned from a sample rendered by Liquid.
    formatMoney(cents) {
      if (!this.moneyFormat) {
        const sample = this.dataset.moneySample || '';
        const match = sample.match(/\d[\d.,'\s  ]*\d/);
        if (match) {
          const number = match[0];
          const digits = number.replace(/\D/g, '');
          const decimals = digits === '1234567';
          this.moneyFormat = {
            prefix: sample.slice(0, match.index),
            suffix: sample.slice(match.index + number.length),
            decimals,
            decimalSeparator: decimals ? number.charAt(number.length - 3) : '',
            thousandsSeparator: /\d/.test(number.charAt(2)) ? '' : number.charAt(2),
          };
        } else {
          this.moneyFormat = false;
        }
      }
      const format = this.moneyFormat;
      if (!format) return (cents / 100).toFixed(2);
      const units = format.decimals ? Math.floor(cents / 100) : Math.round(cents / 100);
      const whole = String(units).replace(/\B(?=(\d{3})+(?!\d))/g, format.thousandsSeparator);
      const fraction = format.decimals ? format.decimalSeparator + String(cents % 100).padStart(2, '0') : '';
      return `${format.prefix}${whole}${fraction}${format.suffix}`;
    }

    /* ---------- Recommendations ---------- */

    async loadRecommendations() {
      const recs = this.querySelector('[data-lc-recs]');
      if (!recs || this.dataset.recsEnabled !== 'true') return;
      const productId = this.dataset.recsProduct || '';
      if (this.recsLoaded && !this.recsStale) {
        this.filterRecs();
        return;
      }
      this.recsLoaded = true;
      this.recsStale = false;
      const token = (this.recsToken = {});
      const limit = parseInt(this.dataset.recsLimit, 10) || 6;
      let cards = [];

      if (productId && this.dataset.recsUrl) {
        try {
          const response = await fetch(
            `${this.dataset.recsUrl}?section_id=${RECS_SECTION}&product_id=${encodeURIComponent(productId)}&limit=${limit + 4}&intent=related`
          );
          if (response.ok) {
            const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
            cards = [...doc.querySelectorAll('[data-lc-rec]')];
          }
        } catch (error) {
          /* fall back to the collection below */
        }
      }
      if (token !== this.recsToken) return;

      const notInCart = (list) => list.filter((card) => !this.isInCart(card.dataset.productId));
      cards = notInCart(cards);
      if (!cards.length) {
        const fallback = this.querySelector('template[data-lc-recs-fallback]');
        if (fallback) cards = notInCart([...fallback.content.querySelectorAll('[data-lc-rec]')]);
      }

      const track = recs.querySelector('[data-lc-recs-track]');
      track.replaceChildren(...cards.slice(0, limit).map((card) => document.importNode(card, true)));
      track.scrollLeft = 0;
      recs.hidden = track.children.length === 0;
      if (!recs.hidden && this.isOpen) this.fadeChildren(track, 120);
      this.updateArrows();
    }

    isInCart(productId) {
      return (this.dataset.inCart || ',').includes(`,${productId},`);
    }

    filterRecs() {
      const recs = this.querySelector('[data-lc-recs]');
      if (!recs) return;
      recs.querySelectorAll('[data-lc-rec]').forEach((card) => {
        if (this.isInCart(card.dataset.productId)) this.removeRec(card);
      });
    }

    removeRec(card) {
      if (card.leaving) return;
      card.leaving = true;
      const finish = () => {
        card.remove();
        const recs = this.querySelector('[data-lc-recs]');
        if (recs && !recs.querySelector('[data-lc-rec]')) recs.hidden = true;
        this.updateArrows();
      };
      if (!card.animate || reducedMotion.matches) return finish();
      card.animate(
        [
          { opacity: 1, transform: 'none' },
          { opacity: 0, transform: 'scale(.94)' },
        ],
        { duration: 280, easing: EASE, fill: 'forwards' }
      ).onfinish = finish;
    }

    scrollRecs(direction) {
      const track = this.querySelector('[data-lc-recs-track]');
      if (!track) return;
      const card = track.querySelector('[data-lc-rec]');
      const step = card ? card.getBoundingClientRect().width + 16 : track.clientWidth * 0.8;
      track.scrollBy({ left: direction * step, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
    }

    updateArrows() {
      const track = this.querySelector('[data-lc-recs-track]');
      if (!track) return;
      const max = track.scrollWidth - track.clientWidth - 2;
      const previous = this.querySelector('[data-lc-recs-nav="-1"]');
      const next = this.querySelector('[data-lc-recs-nav="1"]');
      if (previous) previous.disabled = track.scrollLeft <= 2;
      if (next) next.disabled = track.scrollLeft >= max;
    }
  }

  customElements.define('lovelia-cart-drawer', LoveliaCartDrawer);
})();
